'use client';

import { ArrowLeft, ArrowRight } from 'lucide-react';

import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

/**
 * Cards for the /customer-stories index.
 *
 * The index used to render one card shape thirteen times — every story a
 * square with its photo pushed behind a `black/60` blur so white text could
 * sit on top. Thirteen identical dark tiles gave the page no hierarchy, hid
 * the photography it was paying for, and set every result in 20px type.
 *
 * The replacement takes its structure from the shape of the content rather
 * than from decoration: a story with a cleared photo and two numbers earns a
 * different cell from a story with one number and no image, and an attributed
 * quote is its own cell entirely. Variety comes from span and aspect ratio —
 * the border, the radius and the gap never change.
 *
 * Photography is never a backdrop for text here. It either owns half of a
 * feature card or the whole of a photo card, where only the wordmark and one
 * number sit over it, inside a gradient that is dark enough to read on.
 *
 * The wall runs on a near-black ground, continuing the film stage above it
 * rather than cutting to white one pixel below a full-screen video. Cards are
 * a wash of white over that (`white/[0.04]`, lifting to `/[0.08]` on hover)
 * with a `white/10` hairline — the only way a light card reads on dark without
 * turning into a lightbox. Photographs gain a slow scale on hover, which is
 * the whole of the motion budget: a wall of 27 tiles that all move at once is
 * a screensaver, not a page.
 */

export interface CardStat {
  value: string;
  label: string;
}

interface Media {
  /** Image path, or a complete multi-layer `background-image` value. */
  background: string;
  backgroundColor?: string;
  backgroundSize?: string;
  backgroundPosition?: string;
}

const mediaStyle = (media: Media): React.CSSProperties => ({
  // An already-formed value (possibly several comma-separated layers, as the
  // Prêts composition needs) is passed through rather than wrapped in url().
  backgroundImage: media.background.startsWith('url(')
    ? media.background
    : `url(${media.background})`,
  backgroundColor: media.backgroundColor,
  backgroundSize: media.backgroundSize,
  backgroundPosition: media.backgroundPosition,
});

/**
 * 11px uppercase mono is the label voice across the index — stat captions,
 * the story count, the filters row. Arabic takes neither: the mono face has
 * no Arabic glyphs (it would fall back to a system monospace) and uppercase
 * is a no-op on a script without case. `html[lang='ar']` already neutralises
 * the tracking utility globally, so only the family and case need swapping.
 */
export const MicroLabel = ({
  children,
  isRTL,
  className,
}: {
  children: React.ReactNode;
  isRTL: boolean;
  className?: string;
}) => (
  <span
    className={cn(
      'text-[11px] leading-[1.35]',
      // Arabic inherits the site's own Arabic stack: the mono face has no
      // Arabic glyphs, and 11px caps read as broken words in a joined script.
      isRTL ? 'text-xs' : 'font-mono tracking-[0.1em] uppercase',
      className,
    )}
  >
    {children}
  </span>
);

const Stat = ({
  stat,
  isRTL,
  tone = 'dark',
}: {
  stat: CardStat;
  isRTL: boolean;
  tone?: 'dark' | 'light';
}) => (
  <div className="flex flex-col gap-1">
    <div className="font-serif text-4xl leading-none tabular-nums md:text-5xl">
      {stat.value}
    </div>
    <MicroLabel
      isRTL={isRTL}
      className={tone === 'light' ? 'text-white/70' : 'text-white/50'}
    >
      {stat.label}
    </MicroLabel>
  </div>
);

const Wordmark = ({
  src,
  alt,
  tone = 'dark',
  keepsColor,
  className,
}: {
  src?: string;
  alt: string;
  tone?: 'dark' | 'light';
  /** Skip the white knockout — see the note on the filter below. */
  keepsColor?: boolean;
  className?: string;
}) =>
  // Not every customer has a logo file yet. The name stands in — but set in
  // the same 11px mono the captions use, not in serif. Serif made it read as
  // a second headline stacked above the real one, which is what made the
  // logo-less cards look unlike every other card on the wall.
  !src ? (
    <span
      className={cn(
        'font-mono text-[11px] tracking-[0.1em] uppercase',
        tone === 'light' ? 'text-white/80' : 'text-white/50',
        className,
      )}
    >
      {alt}
    </span>
  ) : (
    <img
      src={src}
      alt={alt}
      className={cn(
        // Height caps a square mark (Snel Een Taxi ships one); width caps a
        // long wordmark. Most hit the width cap and land around 24px tall.
        'block max-h-9 w-auto max-w-[9rem] object-contain object-left',
        // The ground is dark, so a solid-black wordmark has to be knocked out
        // to white. But four of these — Banqy, Nature House, Rewaa, Syarah —
        // are a coloured shape with a white glyph cut out of it, and
        // `brightness-0 invert` turns the shape AND the glyph the same white:
        // the letter vanishes and the mark renders as a blank square. Those
        // keep their own colours, which read fine on near-black.
        !keepsColor && 'brightness-0 invert',
        className,
      )}
    />
  );

const ReadArrow = ({ isRTL }: { isRTL: boolean }) =>
  isRTL ? (
    <ArrowLeft className="size-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
  ) : (
    <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1" />
  );

/**
 * Shared shell: one border, one radius, one hover.
 *
 * These cards used to carry `rounded-[.5rem]`, which the corner-radius system
 * in globals.css flattens to 6px — an arbitrary value states an exact intent,
 * so it is left alone rather than mapped. That is why they read as square.
 * `rounded-2xl` is the card value in that system and resolves to 18px, the
 * same corner as every other card, table and media box on the site.
 *
 * Nothing nested inside these needs the concentric rule: the photo and
 * feature cards carry their image as a background on the clipping box itself,
 * so the box owns the corner and there is no second radius to keep parallel.
 * A card that ever gains an inset child takes `.radius-concentric` with
 * `--inner-radius` and `--inset`, per the note in globals.css.
 */
const shell =
  'group relative flex overflow-hidden rounded-2xl border border-white/10 text-white transition-colors duration-500';

interface StoryCardProps {
  href: string;
  title: string;
  wordmark?: string;
  company: string;
  stats: CardStat[];
  cta: string;
  isRTL: boolean;
  media?: Media;
  /** Shown where a figure would go, for a story that has no cleared one.
   *  Two lines rather than one joined string: an industry name plus a country
   *  wraps raggedly at 11px, and a deliberate second line reads as a caption
   *  block instead of as an accident. */
  industry?: string;
  location?: string;
  /** Logo is a coloured shape with a knocked-out glyph; do not invert it. */
  wordmarkKeepsColor?: boolean;
  /** `white` lifts a card out of the sand run; default is sand. */
  tone?: 'sand' | 'white';
  className?: string;
}

/**
 * Full-width lead card: copy on one side, photo on the other. Two per page —
 * one at the top, one at the mid-point — so the grid restates its rhythm
 * instead of running twenty squares together.
 *
 * With no media it is a copy card across the full width: the headline gets the
 * left half and the numbers move to the right, so the row still reads as a
 * lead rather than as a stretched tile. That is the English state of the two
 * stories whose only picture is a frame of an Arabic-only film — see the note
 * on `backgroundRTLOnly` in the index page.
 */
export const StoryFeatureCard = ({
  href,
  title,
  wordmark,
  company,
  stats,
  cta,
  wordmarkKeepsColor,
  isRTL,
  media,
  className,
}: StoryCardProps) => (
  <Link
    href={href}
    prefetch={true}
    className={cn(
      shell,
      'col-span-1 flex-col bg-white/[0.04] hover:bg-white/[0.07] md:col-span-2 md:flex-row xl:col-span-3',
      className,
    )}
  >
    <div
      className={cn(
        'flex flex-1 flex-col justify-between gap-10 p-6 xl:p-8',
        media ? 'md:w-1/2' : 'md:w-full',
      )}
    >
      <Wordmark src={wordmark} alt={company} keepsColor={wordmarkKeepsColor} />
      <div
        className={cn(
          'flex flex-col gap-6',
          !media && 'md:flex-row md:items-end md:justify-between md:gap-12',
        )}
      >
        <div className="flex flex-col gap-6">
          <h2
            className={cn(
              'font-serif text-2xl leading-[1.15] md:text-3xl xl:text-4xl',
              !media && 'md:max-w-[22ch]',
            )}
          >
            {title}
          </h2>
          <span className="flex w-fit items-center gap-2 text-sm font-medium">
            {cta}
            <ReadArrow isRTL={isRTL} />
          </span>
        </div>
        <div className="flex gap-10">
          {stats.map((stat) => (
            <Stat key={stat.label} stat={stat} isRTL={isRTL} />
          ))}
        </div>
      </div>
    </div>
    {media ? (
      <div
        style={mediaStyle(media)}
        className="aspect-[4/3] w-full bg-cover bg-center bg-no-repeat transition-transform duration-700 ease-out group-hover:scale-[1.03] md:aspect-auto md:w-1/2"
      />
    ) : null}
  </Link>
);

/**
 * Photo card: the image owns the whole cell. Nothing sits on it but the
 * wordmark and one number, over a gradient — no blur, no flat scrim across
 * the middle of the picture.
 */
export const StoryPhotoCard = ({
  href,
  title,
  wordmark,
  company,
  stats,
  wordmarkKeepsColor,
  isRTL,
  media,
  className,
}: StoryCardProps) => (
  <Link
    href={href}
    prefetch={true}
    className={cn(
      shell,
      'aspect-square flex-col justify-between bg-neutral-900 p-5',
      'after:pointer-events-none after:absolute after:inset-0 after:bg-gradient-to-t after:from-black/85 after:via-black/35 after:to-black/25 after:transition-opacity after:duration-300 group-hover:after:opacity-90',
      className,
    )}
  >
    {/* The picture is its own layer so the zoom is a transform, not a
        background-size change. Growing `background-size` to a percentage
        overrides `cover`, and the moment it does the image stops filling the
        card — which is what left black bars along the edges on hover. A
        transform scales what is already covering, and the card clips it.
        Keeping it separate also means a per-card `backgroundSize` override
        (Snel Een Taxi's `auto 115%`) survives the hover. */}
    {media ? (
      <div
        style={mediaStyle(media)}
        className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-700 ease-out group-hover:scale-[1.06]"
      />
    ) : null}

    <div className="relative z-10 flex items-start justify-between gap-3">
      <Wordmark
        src={wordmark}
        alt={company}
        tone="light"
        keepsColor={wordmarkKeepsColor}
      />
      <ReadArrow isRTL={isRTL} />
    </div>
    <div className="relative z-10 flex flex-col gap-5">
      <h2 className="text-base leading-[1.25] font-medium md:text-lg">
        {title}
      </h2>
      {stats[0] ? <Stat stat={stats[0]} isRTL={isRTL} tone="light" /> : null}
    </div>
  </Link>
);

/**
 * Quiet card: no photograph at all. Wordmark, headline, one number. It is the
 * rest between two pictures, and the only card a story without cleared
 * imagery ever needs.
 */
export const StoryQuietCard = ({
  href,
  title,
  wordmark,
  company,
  stats,
  industry,
  location,
  tone = 'sand',
  wordmarkKeepsColor,
  isRTL,
  className,
}: StoryCardProps) => {
  const stat = stats[0];

  return (
    <Link
      href={href}
      prefetch={true}
      className={cn(
        shell,
        'flex-col gap-4 p-5',
        // Ten identical sand squares in a row read as a different page from
        // the one above them, which mixes full-width leads, dark photographs
        // and white quotes. Same components, more rhythm: some cards run two
        // columns, and the tone alternates.
        tone === 'white'
          ? 'bg-white/[0.08] hover:bg-white/[0.12]'
          : 'bg-white/[0.04] hover:bg-white/[0.08]',
        // A card with a number holds the square, so its figure lands on the
        // same baseline as the photo cards either side of it. A card with no
        // number does not: forced square, the bottom two-thirds was empty,
        // and ten of those in a row read as unfinished rather than quiet. It
        // sizes to its content instead.
        //
        // These are always one column. `aspect-square` ties height to width,
        // so a two-column quiet card turned 731px of width into 731px of
        // height — an enormous tile that dragged its whole row with it. Wide
        // cells are for quotes, which derive their own aspect ratio.
        stat ? 'aspect-square' : 'min-h-[15rem]',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <Wordmark
          src={wordmark}
          alt={company}
          keepsColor={wordmarkKeepsColor}
        />
        <ReadArrow isRTL={isRTL} />
      </div>
      {/* Headline sits directly under the wordmark and the number is pushed to
          the floor — bottom-aligning both left a third of the card empty. */}
      <h2
        className={cn(
          'font-serif leading-[1.2]',
          'text-xl md:text-2xl',
        )}
      >
        {title}
      </h2>

      {stat ? (
        <div className="mt-auto">
          <Stat stat={stat} isRTL={isRTL} />
        </div>
      ) : industry ? (
        // Standing in for the number: what the company does and where, in the
        // same 11px mono the stat captions use. Pinned to the floor so the
        // caption line of every card in a row shares one baseline.
        <div className="mt-auto flex flex-col gap-0.5">
          <MicroLabel isRTL={isRTL} className="text-white/50">
            {industry}
          </MicroLabel>
          {location ? (
            <MicroLabel isRTL={isRTL} className="text-white/35">
              {location}
            </MicroLabel>
          ) : null}
        </div>
      ) : null}
    </Link>
  );
};

interface QuoteCardProps {
  href: string;
  /** One paragraph per entry. A wide cell takes two; a square cell takes one. */
  quote: string[];
  attribution: string;
  wordmark?: string;
  company: string;
  /** Logo is a coloured shape with a knocked-out glyph; do not invert it. */
  wordmarkKeepsColor?: boolean;
  isRTL: boolean;
  /** Two columns wide — for the longer quotes. */
  wide?: boolean;
  className?: string;
}

/**
 * A card that is only a customer talking. Attribution is a real named person
 * from the story itself — the unattributed marketing quotes on the homepage
 * carousel are deliberately not eligible.
 */
export const StoryQuoteCard = ({
  href,
  quote,
  attribution,
  wordmark,
  company,
  wordmarkKeepsColor,
  isRTL,
  wide,
  className,
}: QuoteCardProps) => (
  <Link
    href={href}
    prefetch={true}
    className={cn(
      shell,
      'flex-col gap-5 bg-white/[0.04] p-5 hover:bg-white/[0.08]',
      // A wide quote sits in a row with square cards, which cannot stretch —
      // `aspect-square` pins their height to their width. Left to size itself
      // the quote ran taller than them and the row broke: one card ending
      // 84px short of its neighbour. Deriving its height from its width the
      // same way keeps the row flush. Two columns plus the gap over one column
      // is 2 + gap/col ≈ 2.05 at every breakpoint this grid uses.
      wide ? 'aspect-square md:col-span-2 md:aspect-[2.05/1]' : 'aspect-square',
      className,
    )}
  >
    <Wordmark src={wordmark} alt={company} keepsColor={wordmarkKeepsColor} />
    {/* The quote is the card. It starts under the wordmark and the name is
        pushed to the floor, so a short quote does not leave a void above it.
        A wide cell inherits the row height of the squares beside it, which one
        sentence cannot fill — so it carries a second paragraph and centres. */}
    <div
      className={cn(
        'flex flex-col gap-4',
        // No measure cap on a wide quote: it is height-bound now, and the
        // narrower column pushed two paragraphs 49px past the card's floor.
        wide && 'my-auto gap-4',
      )}
    >
      {quote.map((paragraph, i) => (
        <p
          key={i}
          className={cn(
            'font-serif leading-[1.25] text-white/90',
            // Down a step now the card is height-bound.
            wide ? 'text-base md:text-lg' : 'text-lg md:text-xl',
          )}
        >
          {i === 0 ? '“' : null}
          {paragraph}
          {i === quote.length - 1 ? '”' : null}
        </p>
      ))}
    </div>
    <MicroLabel isRTL={isRTL} className="mt-auto text-white/50">
      {attribution}
    </MicroLabel>
  </Link>
);
